import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { FirebaseAuthService } from '@firebase/firebase-auth.service';

import { firstValueFrom, interval, ReplaySubject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';

import { ChatsV2Service } from '@backend-client/services';
import { GoCoreChatPresenceService } from '@shared/services/gocore/chat/gocore-chat-presence.service';
import { FeatureFlagService } from '@shared/services/feature-flag.service';

@Component({
  selector: 'app-therapist-status',
  templateUrl: './therapist-status.component.html',
  styleUrl: './therapist-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatSlideToggle, MatIcon],
})
export class TherapistStatusComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private firebaseAuth = inject(FirebaseAuthService);
  private chatsV2Service = inject(ChatsV2Service);
  private goCoreChatPresenceService = inject(GoCoreChatPresenceService);
  featureFlagService = inject(FeatureFlagService);

  private readonly ONE_MINUTE_IN_MILLISECONDS = 60000;
  private interval = interval(this.ONE_MINUTE_IN_MILLISECONDS);
  private destroySubscription$: ReplaySubject<boolean>;

  public therapistOnlineStatus = new FormControl<boolean>(true);

  ngOnInit(): void {
    void this.initialise();
  }

  private async initialise(): Promise<void> {
    this.setTherapistAsOnline(true, true);

    const activeUser = await firstValueFrom(this.firebaseAuth.user$);
    this.goCoreChatPresenceService
      .getUserPresenceObservable(activeUser?.uid)
      .pipe(
        map((presenceList) => presenceList[0]),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((presence) => {
        const isOnline = this.goCoreChatPresenceService.isUserOnline(presence);
        this.therapistOnlineStatus.patchValue(isOnline);
        this.cdr.markForCheck();
      });

    this.therapistOnlineStatus.valueChanges
      .pipe(debounceTime(600), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.setTherapistAsOnline(value);
      });
  }

  public setTherapistAsOnline(online: boolean, initialise?: boolean): void {
    if (initialise) {
      this.createIntervalForOnlineStatus(online);
    }

    if (online) {
      if (!this.destroySubscription$?.observed) {
        this.createIntervalForOnlineStatus(online);
      }
    } else {
      this.destroySubscription$.next(true);
      this.destroySubscription$.complete();
    }
  }

  private createIntervalForOnlineStatus(_isOnline: boolean): void {
    this.destroySubscription$ = new ReplaySubject<boolean>(1);

    this.interval
      .pipe(
        switchMap(() =>
          this.chatsV2Service.ChatsControllerV2SetAppearOffline({
            appearOffline: false,
          })
        ),
        takeUntil(this.destroySubscription$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroySubscription$.next(true);
    this.destroySubscription$.complete();
  }
}
